import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, MessageCircle, Send, Globe2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import { Reveal, RevealGroup, RevealItem } from "@/components/Reveal";
import { Overline } from "@/components/Bits";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  WORLD_VISAS,
  SCHENGEN_VISAS,
  WHATSAPP_LINK,
  CONTACT,
  FORMSPREE_ENDPOINT,
} from "@/lib/site";

const flagUrl = (code) => `https://flagcdn.com/w320/${code}.png`;

function FlagCard({ name, code }) {
  return (
    <RevealItem>
      <Link
        to="/contact"
        data-testid={`visa-flag-${code}`}
        className="group block overflow-hidden rounded-xl border border-brand-blue/8 bg-white hover:-translate-y-1 hover:shadow-lg transition-all cursor-pointer"
      >
        <div className="aspect-[5/3] overflow-hidden bg-brand-bg">
          <img
            src={flagUrl(code)}
            alt={`${name} flag`}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        </div>
        <p className="px-4 py-3 text-center font-heading font-medium text-brand-blue text-sm">{name}</p>
      </Link>
    </RevealItem>
  );
}

export default function VisaServices() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", destination: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const summary = () =>
    `Visa Enquiry%0A------------%0AName: ${form.name}%0AEmail: ${form.email}%0APhone: ${form.phone}%0ADestination: ${form.destination}%0AMessage: ${form.message}`;

  const emailBody = () =>
    `Visa Enquiry\n------------\nName: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\nDestination: ${form.destination}\nMessage: ${form.message}`;

  const sendEmail = () => {
    window.location.href = `mailto:${CONTACT.emails[0]}?subject=${encodeURIComponent(
      "Visa Enquiry: " + (form.destination || "General")
    )}&body=${encodeURIComponent(emailBody())}`;
  };

  const sendWhatsApp = () => {
    window.open(WHATSAPP_LINK(emailBody()), "_blank");
  };

  const submitFormspree = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ ...form, _subject: "Visa Enquiry: Fly Gowell" }),
      });
      if (res.ok) {
        trackEvent("generate_lead", { form: "visa", destination: form.destination });
        toast.success("Enquiry sent! Our team will be in touch shortly.");
        setForm({ name: "", email: "", phone: "", destination: "", message: "" });
      } else {
        toast.error("Could not send. Please try Email or WhatsApp below.");
      }
    } catch {
      toast.error("Could not send. Please try Email or WhatsApp below.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="visa-services-page">
      {/* Hero / Intro */}
      <section className="relative overflow-hidden pt-40 pb-20 md:pt-48 md:pb-24">
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-brand-blue/[0.06] via-transparent to-brand-green/[0.08]" />
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 relative z-10">
          <Overline>Visa Services</Overline>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 max-w-4xl text-4xl md:text-6xl font-bold tracking-tight text-brand-blue leading-[1.05]"
          >
            Visas for the world, handled with expertise.
          </motion.h1>
          <p className="mt-6 max-w-3xl text-lg md:text-xl text-brand-slate leading-relaxed">
            Embarking on international travel requires meticulous planning. Our visa services
            simplify and expedite the complex process of obtaining visas for destinations
            worldwide. From gathering documentation to navigating embassy requirements, our
            experienced team handles the intricacies so you can focus on the journey ahead.
          </p>
        </div>
      </section>

      {/* Worldwide grid */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 pb-24">
        <Reveal>
          <div className="flex items-center gap-3">
            <Globe2 className="text-brand-ocean" size={26} />
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-brand-blue">
              We Process Visas for Countries Worldwide
            </h2>
          </div>
        </Reveal>
        <RevealGroup className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {WORLD_VISAS.map((c) => (
            <FlagCard key={c.code} {...c} />
          ))}
        </RevealGroup>
        <Reveal delay={0.15}>
          <p className="mt-8 text-lg text-brand-slate italic">
            …and visas for virtually every country around the world.
          </p>
        </Reveal>
      </section>

      {/* Schengen specialists */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-blue to-brand-ocean">
        <div className="grain absolute inset-0" />
        <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 py-24 md:py-32">
          <Reveal>
            <div className="flex items-center gap-3 text-brand-yellow">
              <Sparkles size={24} />
              <span className="text-xs uppercase tracking-[0.25em] font-semibold">Specialists</span>
            </div>
            <h2 className="mt-4 text-3xl md:text-5xl font-bold tracking-tight text-white max-w-2xl">
              Schengen Visa Specialists
            </h2>
            <p className="mt-5 max-w-2xl text-lg text-white/85 leading-relaxed">
              Fly Gowell specializes in Schengen visas, with deep expertise across member states
              and their documentation requirements.
            </p>
          </Reveal>
          <RevealGroup className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
            {SCHENGEN_VISAS.map((c) => (
              <RevealItem key={c.code}>
                <Link to="/contact" data-testid={`schengen-flag-${c.code}`} className="block overflow-hidden rounded-xl bg-white/10 backdrop-blur border border-white/15 hover:bg-white/15 hover:-translate-y-1 transition-all cursor-pointer">
                  <div className="aspect-[5/3] overflow-hidden">
                    <img src={flagUrl(c.code)} alt={`${c.name} flag`} loading="lazy" className="h-full w-full object-cover" />
                  </div>
                  <p className="px-3 py-2.5 text-center font-heading font-medium text-white text-sm">{c.name}</p>
                </Link>
              </RevealItem>
            ))}
          </RevealGroup>
          <Reveal delay={0.15}>
            <p className="mt-8 text-white/70">France, Germany, Italy, Spain, Netherlands, Switzerland, Austria, Belgium, Greece, Portugal and more.</p>
          </Reveal>
        </div>
      </section>

      {/* Enquiry box */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 md:py-32">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-4">
            <Overline>Get Started</Overline>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight text-brand-blue">
              Enquire About Your Visa
            </h2>
            <p className="mt-5 text-lg text-brand-slate leading-relaxed">
              Share your details and destination, and our visa desk will guide you through
              documentation, appointments, and approvals.
            </p>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-8">
            <form onSubmit={submitFormspree} data-testid="visa-enquiry-form" className="rounded-3xl border border-brand-blue/8 bg-white p-8 md:p-10">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <Label className="text-brand-slate">Name</Label>
                  <Input data-testid="visa-name" required value={form.name} onChange={set("name")} placeholder="Your full name" className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-brand-slate">Email</Label>
                  <Input data-testid="visa-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-brand-slate">Phone</Label>
                  <Input data-testid="visa-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-brand-slate">Destination Country</Label>
                  <Input data-testid="visa-destination" value={form.destination} onChange={set("destination")} placeholder="e.g. Germany" className="mt-1.5" />
                </div>
              </div>
              <div className="mt-6">
                <Label className="text-brand-slate">Message</Label>
                <Textarea data-testid="visa-message" value={form.message} onChange={set("message")} placeholder="Tell us about your travel plans..." rows={4} className="mt-1.5" />
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button type="submit" disabled={sending} data-testid="visa-submit" className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue px-6 py-3.5 font-semibold text-white hover:bg-brand-ocean transition-colors disabled:opacity-60">
                  <Send size={17} /> {sending ? "Sending..." : "Submit Enquiry"}
                </button>
                <button type="button" onClick={sendEmail} data-testid="visa-email-btn" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-brand-blue px-6 py-3.5 font-semibold text-brand-blue hover:bg-brand-blue hover:text-white transition-colors">
                  <Mail size={17} /> Send by Email
                </button>
                <button type="button" onClick={sendWhatsApp} data-testid="visa-whatsapp-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-green px-6 py-3.5 font-semibold text-white hover:brightness-110 transition-all">
                  <MessageCircle size={17} /> Enquire on WhatsApp
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
